import moment from "moment";
import { useMemo } from "react";
import { useBlockLayout, useFilters, useGlobalFilter, usePagination, useRowSelect, useSortBy, useTable } from "react-table";
import { useSticky } from "react-table-sticky";
import { CtBillDetailType, CtBillType } from "src/api/billingSchedule/billing-type";
import { BaseButton, BaseTooltip } from "src/components";
import BaseSectionTitle from "src/components/layout/BaseSectionTitle";
import { numberToStringWithComma, ViewYmdFormat } from "src/utils";
import BillTotalList from "../../components/BillTotalList";

const columns: any = [
  {
    // 과세여부 컬럼
    Header: "과세여부",
    accessor: "checkTaxAmount",
    sticky: "left",
    width: 120,
    Cell: ({ row }: any) => {
      const taxLabel = row.original.checkTaxAmount === "tax" ? "과세" : "면세";
      return <span>{taxLabel}</span>;
    },
  },
  {
    // 항목명 컬럼
    Header: "항목명",
    accessor: "itemName",
    sticky: "left",
    width: 160,
    Cell: ({ value }: any) => {
      return <span>{value}</span>;
    },
    Footer: ({ rows }: any) => {
      return <p>합계 : </p>;
    },
  },
  {
    // 공급가 컬럼
    Header: () => {
      return (
        <div className="flex-center font14">
          공급가
          <BaseTooltip
            contents={""}
            tooltip={"'공급가'와 '부가세'를 먼저 입력하면 '소계'가 자동 계산됩니다. 소계에서 부가세를 뺀 금액입니다. (소계 / 1.1, 소수점 이하 절사)"}
            type={"normal"}
            className={"tooltip__trigger-icon"}
          />
        </div>
      );
    },
    accessor: "supplyAmount",
    width: 160,
    Cell: ({ value }: any) => {
      return (
        <div className="flex-center-between">
          <span>{numberToStringWithComma(value)}</span>
          <span className="ml10">원</span>
        </div>
      );
    },
    Footer: ({ rows, supplyTotal }: any) => {
      return (
        <div className="flex-center-between">
          <p>{numberToStringWithComma(supplyTotal)}</p>
          <p className="ml10">원</p>
        </div>
      );
    },
  },
  {
    // 부가세 컬럼
    Header: () => {
      return (
        <div className="flex-center font14">
          부가세
          <BaseTooltip
            contents={""}
            tooltip={"'공급가'와 '부가세'를 먼저 입력하면 '소계'가 자동 계산됩니다. 소계에서 공급가를 뺀 금액입니다. (소계 / 11, 소수점 이하 절상)"}
            type={"normal"}
            className={"tooltip__trigger-icon"}
          />
        </div>
      );
    },
    accessor: "taxAmount",
    width: 160,
    Cell: ({ value }: any) => {
      return (
        <div className="flex-center-between">
          <span>{numberToStringWithComma(value)}</span>
          <span className="ml10">원</span>
        </div>
      );
    },
    Footer: ({ rows, taxTotal }: any) => {
      return (
        <div className="flex-center-between">
          <p>{numberToStringWithComma(taxTotal)}</p>
          <p className="ml10">원</p>
        </div>
      );
    },
  },
  {
    // 소계 컬럼
    Header: () => {
      return (
        <div className="flex-center font14">
          소계
          <BaseTooltip
            contents={""}
            tooltip={"항목의 '소계'를 먼저 입력하면 '공급가'와 '부가세'가 자동 계산됩니다. 공급가와 부가세를 합한 금액입니다. (공급가 + 부가세)"}
            type={"normal"}
            className={"tooltip__trigger-icon"}
          />
        </div>
      );
    },
    accessor: "totalAmount",
    width: 160,
    Cell: ({ value, row, data, setDetailList, bill, showBtn }: any) => {
      return (
        <div className="flex-center-between">
          <span>{numberToStringWithComma(Number(value))}</span>
          <span className="ml10">원</span>
        </div>
      );
    },
    Footer: ({ rows, priceTotal }: any) => {
      return (
        <div className="flex-center-between">
          <p>{numberToStringWithComma(priceTotal)}</p>
          <p className="ml10">원</p>
        </div>
      );
    },
  },
  {
    Header: "비고",
    accessor: "description",
    width: 160,
    Cell: ({ value }: any) => {
      return <span>{value || "-"}</span>;
    },
  },
];

type IssueType = "immediate" | "reserved" | "overdue";

type Props = {
  bill: CtBillType;
  taxInvoiceIssueType: IssueType;
  onClose: () => void;
  onSubmit: () => void;
};

const TaxInvocieDetailSection = ({ bill, taxInvoiceIssueType, onClose, onSubmit }: Props) => {
  // 영업일 확인/조회 API

  const { billDetailList } = bill;

  // 세금계산서 발행 목록 생성
  const taxInvoiceIssueList = useMemo(
    () =>
      billDetailList?.map((detail: CtBillDetailType) => ({
        ...detail,
        isAutoTaxUse: false,
        checkTaxAmount: detail.taxAmount > 0 ? "tax" : "nontax",
      })) || [],
    [billDetailList],
  );

  // 공급가 총액 계산
  const supplyTotal = useMemo(() => {
    return taxInvoiceIssueList.filter((detail) => !detail.isDeleted).reduce((sum, detail) => sum + (detail.supplyAmount || 0), 0);
  }, [taxInvoiceIssueList]);

  // 부가세 총액 계산
  const taxTotal = useMemo(() => {
    return taxInvoiceIssueList.filter((detail) => !detail.isDeleted).reduce((sum, detail) => sum + (detail.taxAmount || 0), 0);
  }, [taxInvoiceIssueList]);

  // 총액 계산
  const priceTotal = useMemo(() => supplyTotal + taxTotal, [supplyTotal, taxTotal]);

  const { getTableProps, getTableBodyProps, headerGroups, footerGroups, rows, prepareRow } = useTable(
    {
      columns: columns,
      data: taxInvoiceIssueList,
      supplyTotal,
      taxTotal,
      priceTotal,
    },
    useBlockLayout,
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    useSticky,
  );

  // columns width 의 총 합 (table witdh 를 구하기 위함)
  const tableWidth = useMemo(() => {
    let totalWidth = 0;
    headerGroups.forEach((headerGroup) => {
      headerGroup.headers.forEach((header) => (totalWidth += Number(header?.width || 0)));
    });
    return totalWidth;
  }, [headerGroups]);

  return (
    <>
      <section className="base-abstract-modal__title flex-center-between border-bottom-gray ">
        {(taxInvoiceIssueType === "immediate" || taxInvoiceIssueType === "overdue") && (
          <div className="flex-direction-column">
            <h1>세금계산서/계산서 즉시 발행 하시겠습니까?</h1>
            {taxInvoiceIssueType === "overdue" && <p className="text-red font14 mt15">선택한 작성일자로 발행시 가산세가 발생할 수 있습니다.</p>}
          </div>
        )}
        {taxInvoiceIssueType === "reserved" && (
          <>
            <h1>세금계산서/계산서 예약 발행됩니다.</h1>
          </>
        )}
      </section>

      <section className="base-abstract-modal__contents ">
        <article className="contents-container__wrap-article">
          <div>
            <BaseSectionTitle title="기본정보" className="pb20" noHashLink />
            <section className="contents-container__grid">
              <div className="contents-container__grid-index">
                <p>청구서명</p>
              </div>
              <div className="contents-container__grid-contents">
                <p>{bill.billTitle || "-"}</p>
              </div>
            </section>
            <div className="flex-center">
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p>산출기간</p>
                </div>
                <div className="contents-container__grid-contents">
                  <p>
                    {moment(bill.billStartTime).format(ViewYmdFormat.YYYY_MM_DD) +
                      " ~ " +
                      moment(bill.billEndTime).format(ViewYmdFormat.YYYY_MM_DD) || "-"}
                  </p>
                </div>
              </section>
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p>산출기간 비고</p>
                </div>
                <div className="contents-container__grid-contents">
                  <p>{bill.billDescription || "-"}</p>
                </div>
              </section>
            </div>
            <div className="flex-center">
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <div className="flex-center">
                    <p>납부 기간</p>
                    <BaseTooltip
                      tooltip={"필요 시, 납부 종료일이 공휴일인 경우 다음 영업일을 확인하여 납부기한을 변경해 주세요."}
                      type={"normal"}
                      className={"tooltip__trigger-icon"}
                    />
                  </div>
                </div>
                <div className="contents-container__grid-contents">
                  <p>
                    {moment(bill.billIssueDate).format(ViewYmdFormat.YYYY_MM_DD) +
                      " ~ " +
                      moment(bill.billDueDate).format(ViewYmdFormat.YYYY_MM_DD) || "-"}
                  </p>{" "}
                </div>
              </section>
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p>납부 기간 비고</p>
                </div>
                <div className="contents-container__grid-contents">
                  <p>{bill.amountDescription || "-"}</p>
                </div>
              </section>
            </div>
            <div className="flex-center">
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <div className="flex-center">
                    <p>납부 금액</p>
                    <BaseTooltip
                      tooltip={"납부 금액은 세부 내역의 합계를 자동으로 계산하여 출력됩니다."}
                      type={"normal"}
                      className={"tooltip__trigger-icon"}
                    />
                  </div>
                </div>
                <div className="contents-container__grid-contents">
                  <p>{numberToStringWithComma(priceTotal) + " 원" || "-"}</p>
                </div>
              </section>
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p>결제수단</p>
                </div>
                <div className="contents-container__grid-contents">
                  <p>{bill.pgcode === "creditcard" ? "신용카드" : bill.pgcode === "banktransfer" ? "계좌이체" : "-"}</p>
                </div>
              </section>
            </div>

            <section className="contents-container__grid">
              <div className="contents-container__grid-index">
                <div className="flex-center">
                  <p>계산서 작성일</p>
                  <BaseTooltip
                    tooltip={
                      "선택한 계산서 작성일로 전자세금계산서/전자계산서에 작성일자로 표기됩니다.• 선택한 계산서 작성일로 예약 발행 하시려면, 관리비 빌링 현황 화면에서 [발행] 버튼을 클릭해 주세요.• 현재일이 작성일보다 뒤인 경우, [발행] 버튼을 클릭하시면 즉시 발행됩니다."
                    }
                    type={"normal"}
                    className={"tooltip__trigger-icon"}
                  />
                </div>
              </div>
              <div className="contents-container__grid-contents">
                <p>{moment(bill.taxInvoiceIssueDate).format(ViewYmdFormat.YYYY_MM_DD_WEEK_HH_MM_SS) || "-"}</p>
              </div>
            </section>
          </div>
          <section className="base-abstract-modal__title flex-center-between pa0">
            <div>
              <BaseSectionTitle title="세부내역" className="py20" noHashLink />
            </div>

            <p className="pr30 font14 text-primary3">* 세부 항목을 입력 후 저장하시면, 총 합계가 여기에 표시됩니다.</p>
          </section>
          <div className="">
            {/* 기본정보 */}
            <BillTotalList supplyTotal={supplyTotal} taxTotal={taxTotal} priceTotal={priceTotal} />

            {rows.length > 0 && (
              <div {...getTableProps()} className="base-table sticky mb30">
                <div className="header">
                  {headerGroups.map((headerGroup) => (
                    <div {...headerGroup.getHeaderGroupProps()} className="base-table__tr">
                      {headerGroup.headers.map((header) => {
                        return (
                          <div {...header.getHeaderProps()} className="base-table__th">
                            {header.render("Header")}
                            {/* <div className="ic_sort"></div> */}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
                <div {...getTableBodyProps()} className="body">
                  {rows.map((row: any) => {
                    prepareRow(row);
                    if (row.original.isDeleted === false) {
                      return (
                        <div {...row.getRowProps()} className={`base-table__tr ${row.values.floor ? "bg-gray100" : ""}`}>
                          {row.cells.map((cell: any) => {
                            return (
                              <div {...cell.getCellProps()} className="base-table__td">
                                {cell.render("Cell")}
                              </div>
                            );
                          })}
                        </div>
                      );
                    }
                  })}
                  {rows.length === 0 && (
                    <div className="base-table__tr table-cursor-unset" style={{ width: tableWidth }}>
                      <div className="base-table__td w-100 text-center">
                        <div className="w-100">{<span>데이터가 없습니다.</span>}</div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="footer">
                  {footerGroups.map((footerGroup) => (
                    <div {...footerGroup.getHeaderGroupProps()} className="base-table__tr">
                      {footerGroup.headers.map((column) => (
                        <div {...column.getHeaderProps()} className="base-table__td base-table-footer">
                          {column.render("Footer")}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </section>
      <section className="base-abstract-modal__btn-wrap ">
        <div className="flex-center">
          <BaseButton title="닫기" className="color-white mr10" onClick={onClose} />
          {taxInvoiceIssueType !== "reserved" && <BaseButton title={"즉시발행"} onClick={onSubmit} />}
        </div>
      </section>
    </>
  );
};
export default TaxInvocieDetailSection;
